import { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { ALL_ROUTES } from 'config/routes';
import useFetchList from 'hooks/useFetchList';
import { USERS_MANAGING_COLUMNS } from '../constants';
import QhsTable from '../../Shared/Table/Table';
import { convertDataFromApi, useGenerateColumns } from '../../Shared/Table/helpers';
import { useSelector, useDispatch } from 'react-redux';
import TableTitle from '../../Shared/Table/widgets/TableTitle';
import TablePageContainer from '../../Shared/Table/widgets/TablePageContainer';
import AddButton from '../../Shared/Table/widgets/AddButton';
import UserEditModal from '../Item/widgets/shared/UserModals/UserEditModal';
import CreateUserModal from '../Item/widgets/CreateUserModal/CreateUserModal';
import {
  TABLE_KEYS,
  navigateToRoute,
  setPreselectedAccountFromGlobal,
  updateCanShowShimmer,
  setCreateUserStep,
  setPreFilledUserData,
  setCheckedEmail,
  setExistingUser,
} from '../../../redux_store';
import { NULL_QUERY_DELAY } from '../../Shared/constants';
import AccountSelector from '../../Shared/Table/widgets/TopBar/AccountSelector';
import TableActions from './widgets/TableActions';
import RemoveUserModal from './widgets/RemoveUserModal';

const GlobalUsersManagement = () => {
  const {
    en: { titles },
  } = useSelector(state => state.i18n);
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    metadata: { currentAccount },
    isDistributionSponsor,
  } = useSelector(state => state.user);

  // Get preselected account from query parameters
  const preselectedAccountUuid = searchParams.get('preselectedAccountUuid');
  const preselectedAccountName = searchParams.get('preselectedAccountName');
  const autoOpenCreateModal = searchParams.get('autoOpenCreateModal');

  const tableKey = TABLE_KEYS.usersManagingTable;
  const columns = useGenerateColumns(USERS_MANAGING_COLUMNS, tableKey, {}, {}).columns;
  const fields = columns.map(columnData => columnData.accessorKey).sort().join();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [openedFromAccountCreation, setOpenedFromAccountCreation] = useState(false);
  const [defaultAccountForCreate, setDefaultAccountForCreate] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isRemoveUserModalOpen, setIsRemoveUserModalOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);

  const targetAccountUuid = preselectedAccountUuid || currentAccount.uuid;
  const apiEndpoint = `/accounts/${targetAccountUuid}/users/search`;

  const {
    items,
    totalCount,
    reloadData,
    clearFilter,
    loading: dataLoading,
    onLoadMoreRows,
    sort,
    onSort,
    filter,
    onFilter,
    fetchError,
  } = useFetchList({
    baseEndpoint: apiEndpoint,
    baseParams: { fields: fields, includeCurrentAccount: isDistributionSponsor },
    tableKey,
    canLoad: fields.length,
  });

  const [prevTotalCount, setPrevTotalCount] = useState(totalCount);

  useEffect(() => {
    if (prevTotalCount !== totalCount) {
      setPrevTotalCount(totalCount);
    }
  }, [filter, totalCount]);

  // Sync selectedAccount with query parameters
  useEffect(() => {
    if (
      preselectedAccountUuid &&
      preselectedAccountName &&
      !selectedAccount &&
      isDistributionSponsor
    ) {
      const preselectedAccount = {
        uuid: preselectedAccountUuid,
        businessName: preselectedAccountName,
      };
      setSelectedAccount(preselectedAccount);
      setDefaultAccountForCreate(preselectedAccount);
      // Also set in Redux for user account management
      dispatch(setPreselectedAccountFromGlobal(preselectedAccountUuid));
    } else if (!preselectedAccountUuid && selectedAccount) {
      // URL params were cleared (e.g. navigation via side menu) - reset selector
      setSelectedAccount(null);
      dispatch(setPreselectedAccountFromGlobal(null));
    }
  }, [
    preselectedAccountUuid,
    preselectedAccountName,
    selectedAccount,
    isDistributionSponsor,
    dispatch,
  ]);

  // Auto-open create modal if requested
  useEffect(() => {
    if (autoOpenCreateModal === 'true' && selectedAccount && !isCreateModalOpen) {
      setOpenedFromAccountCreation(true);
      setIsCreateModalOpen(true);

      // Clean up the URL parameter after opening modal
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('autoOpenCreateModal');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [autoOpenCreateModal, selectedAccount, isCreateModalOpen, searchParams, setSearchParams]);

  const handleRowClick = data => {
    const { userUUID } = data;

    dispatch(
      navigateToRoute({
        route: {
          path: ALL_ROUTES.user.accountsManagement.path,
          params: {
            currentAccountUuid: currentAccount.uuid,
            userUuid: userUUID,
          },
          title: titles.manageUserAccounts,
        },
      })
    );
  };

  const handleAddUser = () => {
    setIsCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setOpenedFromAccountCreation(false);
    setDefaultAccountForCreate(null);
  };

  const handleEditExistingUser = existingUser => {
    // Close create modal first
    setIsCreateModalOpen(false);

    if (isDistributionSponsor) {
      // For DS accounts: Navigate to the existing user's account management page
      // UserAccountsManagement will fetch the user data using the userUuid
      dispatch(
        navigateToRoute({
          route: {
            path: ALL_ROUTES.user.accountsManagement.path,
            params: {
              currentAccountUuid: currentAccount.uuid,
              userUuid: existingUser.uuid,
            },
            title: titles.manageUserAccounts,
          },
        })
      );
    } else {
      // For non-DS accounts: Check if user already exists in our account
      // User exists in current account if they have role or userPermissions
      const userExistsInCurrentAccount = existingUser.source === 'currentAccount';

      if (userExistsInCurrentAccount) {
        // User already exists in our account - navigate to user management page
        dispatch(
          navigateToRoute({
            route: {
              path: ALL_ROUTES.user.accountsManagement.path,
              params: {
                currentAccountUuid: currentAccount.uuid,
                userUuid: existingUser.uuid,
              },
              title: titles.manageUserAccounts,
            },
          })
        );
      } else {
        // User exists in system but not in our account - proceed with add to account flow
        // Set the user data in Redux for Formik to use
        const userDataForFormik = {
          firstName: existingUser.firstName || '',
          lastName: existingUser.lastName || '',
          phone: existingUser.phone || '', // API might not return phone, default to empty
        };

        // Store pre-filled data in Redux
        dispatch(setPreFilledUserData(userDataForFormik));

        // Also set the checked email and existing user for consistency
        dispatch(setCheckedEmail(existingUser.email));
        dispatch(setExistingUser(existingUser));

        // Set the step to 4 (which is step 3 for non-DS accounts - Account Settings)
        dispatch(setCreateUserStep(4));

        // Reopen the modal - it will now show step 4 (Account Settings) with pre-filled data
        setTimeout(() => {
          setIsCreateModalOpen(true);
        }, 100); // Small delay to ensure modal closed properly first
      }
    }
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingUser(null);
  };

  const handleDeleteUser = userData => {
    setDeletingUser(userData);
    setIsRemoveUserModalOpen(true);
  };

  const handleCloseRemoveUserModal = () => {
    setIsRemoveUserModalOpen(false);
    setDeletingUser(null);
  };

  const handleUserDeleted = () => {
    // Reload the users table after successful deletion
    dispatch(updateCanShowShimmer(true));
    reloadData();
  };

  const handleUserCreated = newUser => {
    // Close create modal
    setIsCreateModalOpen(false);

    // Only navigate to user edit page for DS accounts
    // For non-DS accounts, everything is already configured, so just close the modal
    if (isDistributionSponsor && newUser && newUser.uuid) {
      dispatch(
        navigateToRoute({
          route: {
            path: ALL_ROUTES.user.accountsManagement.path,
            params: {
              currentAccountUuid: currentAccount.uuid,
              userUuid: newUser.uuid,
            },
            title: titles.manageUserAccounts,
          },
        })
      );
    } else if (!isDistributionSponsor) {
      dispatch(updateCanShowShimmer(true));
      reloadData();
    }
  };

  const reloadReportableList = hasFilter => {
    if (!hasFilter) {
      clearFilter();
    } else {
      reloadData({ delay: NULL_QUERY_DELAY });
    }
  };

  const handleAccountChange = account => {
    setSelectedAccount(account);

    // Also update Redux store for user account management
    if (account) {
      dispatch(setPreselectedAccountFromGlobal(account.uuid));
      // Update query parameters to trigger useFetchList reload
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('preselectedAccountUuid', account.uuid);
      newSearchParams.set('preselectedAccountName', account.businessName);
      setSearchParams(newSearchParams);
    } else {
      dispatch(setPreselectedAccountFromGlobal(null));
      // Clear account-related query parameters
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('preselectedAccountUuid');
      newSearchParams.delete('preselectedAccountName');
      setSearchParams(newSearchParams);
    }
  };
  const users = useMemo(() => items && convertDataFromApi(items, tableKey, filter), [items]);

  const hasFilters = useMemo(
    () =>
      !!filter?.reduce((accumulator, { value }) => {
        return accumulator + value.toString().replaceAll(' ', '').length;
      }, 0),
    [filter]
  );

  // Create AccountSelector widget only for Distribution Sponsor accounts
  const accountSelectorWidget = useMemo(() => {
    if (!isDistributionSponsor) {
      return null;
    }

    return (
      <AccountSelector
        selectedAccount={selectedAccount}
        onAccountChange={handleAccountChange}
        includeCurrentAccount={true}
      />
    );
  }, [isDistributionSponsor, selectedAccount, handleAccountChange]);

  return (
    <TablePageContainer>
      <QhsTable
        tableKey={tableKey}
        columns={columns}
        data={users || []}
        fetchNextPage={onLoadMoreRows}
        isFetching={dataLoading}
        isLoading={dataLoading && !users}
        totalRowCount={totalCount}
        sorting={sort}
        setSorting={onSort}
        filter={filter}
        setFilters={onFilter}
        showRefreshTableButton
        reloadData={reloadReportableList}
        onRowClick={handleRowClick}
        fetchError={fetchError}
        tableTitle={
          <TableTitle
            titleText={titles.globalUsersManagement}
            count={totalCount || totalCount === 0 ? totalCount : null}
            countFiltered={hasFilters}
          />
        }
        menuActions={<AddButton label={titles.user} onClick={handleAddUser} />}
        customFilterWidget={accountSelectorWidget}
        renderRowActionMenuItems={({ row, setAnchorEl }) => (
          <TableActions row={row} setAnchorEl={setAnchorEl} onDeleteUser={handleDeleteUser} />
        )}
      />

      <CreateUserModal
        open={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        onUserCreated={handleUserCreated}
        onEditUser={handleEditExistingUser}
        preselectedAccount={
          openedFromAccountCreation && preselectedAccountUuid && preselectedAccountName
            ? { uuid: preselectedAccountUuid, businessName: preselectedAccountName }
            : null
        }
        defaultSelectedAccount={defaultAccountForCreate}
      />

      <UserEditModal
        open={isEditModalOpen}
        handleClose={handleCloseEditModal}
        accountUuid={currentAccount.uuid}
        userData={editingUser}
        userUuid={editingUser?.uuid}
        onSave={() => {
          // Reload the users table after edit
          reloadData();
        }}
      />

      <RemoveUserModal
        open={isRemoveUserModalOpen}
        onClose={handleCloseRemoveUserModal}
        userUuid={deletingUser?.userUUID}
        currentAccountUuid={currentAccount.uuid}
        onUserDeleted={handleUserDeleted}
      />
    </TablePageContainer>
  );
};

export default GlobalUsersManagement;
